import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

export default function StudentAttendanceReport() {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [batches, setBatches] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    };

    const formatDateForDisplay = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${day}-${month}-${year}`;
    };

    // Format Date object to YYYY-MM-DD string
    const formatDateForComparison = (date) => {
        if (!(date instanceof Date)) {
            return date; // If it's already a string, return it as is
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        return Array.from({ length: daysInMonth }, (_, i) => {
            return new Date(year, month, i + 1);
        });
    };

    const fetchBatches = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) throw new Error('No access token found');

            const response = await fetch(`${process.env.REACT_APP_API}/api/course/batches/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            setBatches(data);
        } catch (error) {
            console.error('Error fetching batches:', error);
        }
    };

    const fetchAttendanceReport = async () => {
        setLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');
            const month = formatDateForAPI(selectedDate);
            const url = `${process.env.REACT_APP_API}/api/course/student-attendance-report/?month=${month}${
                selectedCourseId ? `&course_id=${selectedCourseId}` : ''
            }`;

            const response = await axios.get(url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            setAttendanceData(response.data);
        } catch (error) {
            console.error('Error fetching attendance report:', error);
            setError('Error fetching attendance data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBatches();
    }, []);

    useEffect(() => {
        fetchAttendanceReport();
    }, [selectedDate, selectedCourseId]);

    const handleCourseChange = (event) => {
        setSelectedCourseId(event.target.value);
    };

    const getCurrentMonthString = () => {
        const year = selectedDate.getFullYear();
        const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    };

    const handleDateChange = (e) => {
        const [year, month] = e.target.value.split('-');
        setSelectedDate(new Date(year, month - 1));
    };

    const getUniqueStudents = () => {
        const studentsMap = new Map();
        attendanceData.forEach(record => {
            studentsMap.set(record.student_code, record.student_name);
        });
        return Array.from(studentsMap, ([code, name]) => ({ code, name }));
    };

    const findAttendanceForDate = (studentCode, date) => {
        const formattedDate = formatDateForComparison(date);
        const record = attendanceData.find(
            r => r.student_code === studentCode && r.date === formattedDate
        );
        return record || null;
    };

    const renderAttendanceCell = (attendance) => {
        if (!attendance) {
            return <td className="text-center">-</td>;
        }

        return (
            <td
                className="text-center"
                style={{
                    backgroundColor: attendance.status === 'Late' ? 'rgba(255, 0, 0, 0.1)' : 'transparent'
                }}
            >
                {/* <div>{attendance.status}</div> */}
                {/* <div className="d-flex justify-content-center align-items-center"> */}
                    {attendance.clock_in || '-'} - {attendance.clock_out || '-'}
                {/* </div> */}
            </td>
        );
    };

    const daysInMonth = getDaysInMonth(selectedDate);
    const uniqueStudents = getUniqueStudents();

    return (
        <div className="p-3 px-4 overflow-x-auto">
            <h3 className="mb-4">Attendance Report</h3>

            <div className="mb-4 w-100 d-flex justify-content-between">
                <input
                    type="month"
                    value={getCurrentMonthString()}
                    onChange={handleDateChange}
                    className="form-control w-auto"
                />
                <select
                    className="form-select w-auto"
                    value={selectedCourseId}
                    onChange={handleCourseChange}
                >
                    <option value="">All Courses</option>
                    {batches.map(batch => (
                        <option key={batch.id} value={batch.course.id}>
                            {batch.course.course_name}
                        </option>
                    ))}
                </select>
            </div>

            {loading && <div className="mb-4">Loading attendance data...</div>}
            {error && <div className="mb-4 text-danger">{error}</div>}

            {!loading && !error && attendanceData.length > 0 && (
                <div className="position-relative" style={{ zIndex: '1' }}>
                    <style>
                        {`
                            .table-hover tbody tr:hover td {
                                background-color: rgba(0, 123, 255, 0.1) !important;
                                z-index: 5;
                            }
                            .table-hover tbody tr:hover td.bg-white {
                                background-color: rgba(255, 255, 255, 1) !important;
                            }
                            .table-hover tbody tr td {
                                z-index: 5;
                            }
                            .table-hover tbody tr td.bg-white {
                                z-index: 10;
                            }
                        `}
                    </style>
                    <div
                        className="overflow-auto"
                        style={{ maxHeight: "66vh" }}
                        ref={scrollContainerRef}
                    >
                        <table className="table table-bordered table-hover">
                            <thead>
                                <tr>
                                    <th
                                        className={`text-white ${!isMobile ? "position-sticky start-0" : ""}`}
                                        style={{
                                            width: "12rem",
                                            zIndex: 20,
                                            backgroundColor: '#120e4d',
                                            ...(isMobile ? {} : { position: 'sticky', left: 0, top: 0 })
                                        }}
                                    >
                                        Name
                                    </th>
                                    {daysInMonth.map((day, index) => (
                                        <th
                                            key={index}
                                            className="text-white position-sticky top-0 text-center"
                                            style={{ backgroundColor: '#120e4d', minWidth: '120px' }}
                                        >
                                            {formatDateForDisplay(day)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {uniqueStudents.map((student, index) => (
                                    <tr key={index}>
                                        <td
                                            className={`${!isMobile ? "position-sticky start-0" : ""} bg-white`}
                                            style={{
                                                width: "12rem",
                                                zIndex: 10,
                                                ...(isMobile ? {} : { position: 'sticky', left: 0 })
                                            }}
                                        >
                                            {student.code} - {student.name}
                                            {/* <div className="small text-muted">Code: {student.code}</div> */}
                                        </td>
                                        {daysInMonth.map((day, dayIndex) => {
                                            const dateStr = formatDateForComparison(day);
                                            const attendance = findAttendanceForDate(student.code, day);
                                            return renderAttendanceCell(attendance);
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && !error && attendanceData.length === 0 && (
                <div className="p-4 text-center">No attendance data available</div>
            )}
        </div>
    );
}